import React, { useCallback, useEffect, useState } from "react";
import StaffNavBar from "../components/StaffNavBar";
import {
  AdminPageBody,
  AdminPanel,
  AdminLoadingState,
  adminButton,
  adminPrimaryButton,
  adminDangerButton,
} from "../components/AdminPageShell";
import { useConfirmDialog } from "../components/ConfirmDialogProvider";
import { useStaffMarketplace } from "../store/staff";

const MARKETPLACE_TYPES = [
  { key: "project-topics", label: "Project Topics" },
  { key: "seminar-topics", label: "Seminar Topics" },
  { key: "research-materials", label: "Research Materials" },
  { key: "manuals", label: "Manuals" },
];

const RESOURCE_STATUSES = [
  "",
  "PENDING",
  "APPROVED",
  "REJECTED",
  "DRAFT",
  "DISABLED",
  "WITHDRAWN",
];

const PURCHASE_STATUSES = [
  "",
  "COMPLETED",
  "PENDING",
  "PROCESSING",
  "FAILED",
  "CANCELLED",
  "REFUNDED",
  "PARTIALLY_REFUNDED",
];

const PURCHASE_TYPES = [
  "",
  "COURSE_PURCHASE",
  "BOOK_PURCHASE",
  "JOURNAL_PURCHASE",
  "LEARNING_PATH_PURCHASE",
  "PROJECT_TOPIC_PURCHASE",
  "SEMINAR_TOPIC_PURCHASE",
  "RESEARCH_MATERIAL_PURCHASE",
  "MANUAL_PURCHASE",
  "CART_PURCHASE",
];

const ACADEMIC_LEVELS = ["", "ND", "HND", "NCE", "BSC", "MSC", "PGD", "PHD", "OTHER"];
const FILE_FORMATS = ["", "PDF", "DOCX", "OTHER"];

const inputClass =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-[#16213A] focus:ring-2 focus:ring-[#16213A]/15";

const fmtDate = (value) => (value ? new Date(value).toLocaleString() : "—");
const fmtMoney = (value) =>
  value === undefined || value === null || Number.isNaN(Number(value))
    ? "—"
    : `₦${Number(value).toLocaleString()}`;
const statusClass = (status) => {
  switch (String(status || "").toUpperCase()) {
    case "APPROVED":
    case "COMPLETED":
      return "bg-emerald-100 text-emerald-700";
    case "PENDING":
    case "PROCESSING":
    case "DRAFT":
      return "bg-amber-100 text-amber-700";
    case "REJECTED":
    case "FAILED":
    case "DISABLED":
      return "bg-red-100 text-red-700";
    default:
      return "bg-slate-100 text-slate-600";
  }
};

const initialForm = {
  title: "",
  summary: "",
  description: "",
  abstract: "",
  chapterOnePreview: "",
  whatIsIncluded: "",
  academicLevel: "",
  fieldOfStudy: "",
  fileFormat: "",
  coverImage: "",
  price: "",
};

export default function StaffMarketplace() {
  const confirm = useConfirmDialog();
  const {
    staffMarketplaceResources,
    staffMarketplaceResourcesLoading,
    staffMarketplaceResourcesError,
    staffMarketplaceResourcesTotal,
    staffMarketplaceResourcesPages,
    staffMarketplaceResourceDetailLoading,
    staffMarketplaceResourceDetailError,
    fetchStaffMarketplaceResources,
    fetchStaffMarketplaceResource,
    updateStaffMarketplaceResource,
    approveStaffMarketplaceResource,
    rejectStaffMarketplaceResource,
    restoreStaffMarketplaceResource,
    withdrawStaffMarketplaceResource,
    staffMarketplacePurchases,
    staffMarketplacePurchasesLoading,
    staffMarketplacePurchasesError,
    staffMarketplacePurchasesTotal,
    staffMarketplacePurchasesPages,
    fetchStaffMarketplacePurchases,
    revokeStaffMarketplacePurchase,
    restoreStaffMarketplacePurchase,
    staffMarketplaceMutationLoading,
    staffMarketplaceMutationError,
  } = useStaffMarketplace();

  const [tab, setTab] = useState("resources");
  const [activeType, setActiveType] = useState(MARKETPLACE_TYPES[0].key);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);

  const [purchaseSearch, setPurchaseSearch] = useState("");
  const [purchaseStatus, setPurchaseStatus] = useState("");
  const [purchaseType, setPurchaseType] = useState("");
  const [purchasePage, setPurchasePage] = useState(1);

  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [message, setMessage] = useState("");

  const showMessage = useCallback((text) => {
    setMessage(text);
    setTimeout(() => setMessage(""), 5000);
  }, []);

  const loadResources = useCallback(async () => {
    const result = await fetchStaffMarketplaceResources(activeType, {
      page,
      limit: 10,
      ...(search ? { search } : {}),
      ...(status ? { status } : {}),
      sort,
    });
    if (!result.success) showMessage(result.error || "Failed to load resources.");
  }, [
    activeType,
    fetchStaffMarketplaceResources,
    page,
    search,
    showMessage,
    sort,
    status,
  ]);

  const loadPurchases = useCallback(async () => {
    const result = await fetchStaffMarketplacePurchases({
      page: purchasePage,
      limit: 10,
      ...(purchaseSearch ? { search: purchaseSearch } : {}),
      ...(purchaseStatus ? { status: purchaseStatus } : {}),
      ...(purchaseType ? { type: purchaseType } : {}),
    });
    if (!result.success) showMessage(result.error || "Failed to load purchases.");
  }, [
    fetchStaffMarketplacePurchases,
    purchasePage,
    purchaseSearch,
    purchaseStatus,
    purchaseType,
    showMessage,
  ]);

  useEffect(() => {
    loadResources();
  }, [loadResources]);

  useEffect(() => {
    loadPurchases();
  }, [loadPurchases]);

  const runMutation = async (mutation, successMessage, reload = loadResources) => {
    const result = await mutation();
    if (!result.success) {
      showMessage(result.error || "Action failed.");
      return false;
    }
    showMessage(successMessage);
    await reload();
    return true;
  };

  const openDetail = async (resource) => {
    setSelected({ ...resource });
    setForm(
      Object.fromEntries(
        Object.keys(initialForm).map((key) => [
          key,
          key === "whatIsIncluded"
            ? (resource.whatIsIncluded || []).join("\n")
            : resource[key] ?? "",
        ])
      )
    );
    const result = await fetchStaffMarketplaceResource(activeType, resource.id);
    if (result.success && result.resource) {
      const detail = result.resource;
      setSelected({ ...resource, ...detail });
      setForm(
        Object.fromEntries(
          Object.keys(initialForm).map((key) => [
            key,
            key === "whatIsIncluded"
              ? (detail.whatIsIncluded || []).join("\n")
              : detail[key] ?? "",
          ])
        )
      );
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!selected) return;
    const payload = {};
    Object.entries(form).forEach(([key, value]) => {
      if (key === "whatIsIncluded") {
        const list = String(value)
          .split("\n")
          .map((entry) => entry.trim())
          .filter(Boolean);
        if (list.length) payload.whatIsIncluded = list;
        return;
      }
      if (key === "price") {
        if (value !== "" && !Number.isNaN(Number(value))) payload.price = Number(value);
        return;
      }
      const text = String(value ?? "").trim();
      if (!text) return;
      payload[key] = text;
    });

    const result = await updateStaffMarketplaceResource(activeType, selected.id, payload);
    if (!result.success) {
      showMessage(result.error || "Failed to update resource.");
      return;
    }
    showMessage("Marketplace resource updated.");
    setSelected(null);
    await loadResources();
  };

  const handleApprove = (resource) =>
    confirm({
      title: `Approve "${resource.title}"?`,
      description: "The resource will be published to the marketplace.",
      confirmLabel: "Approve",
      onConfirm: async () => {
        const ok = await runMutation(
          () => approveStaffMarketplaceResource(activeType, resource.id),
          "Marketplace resource approved."
        );
        if (!ok) throw new Error("Failed to approve resource.");
      },
    });

  const handleReject = (resource) => {
    const reason = window.prompt(`Reason for rejecting "${resource.title}"?`, "");
    if (reason === null) return;
    return runMutation(
      () => rejectStaffMarketplaceResource(activeType, resource.id, reason.trim()),
      "Marketplace resource rejected."
    );
  };

  const handleDetailReject = async () => {
    if (!selected) return;
    const reason = window.prompt(`Reason for rejecting "${selected.title}"?`, "");
    if (reason === null) return;
    const trimmed = reason.trim();
    if (!trimmed) return;
    const ok = await runMutation(
      () => rejectStaffMarketplaceResource(activeType, selected.id, trimmed),
      "Marketplace resource rejected."
    );
    if (ok) setSelected(null);
  };

  const handleWithdraw = (resource) =>
    confirm({
      title: `Withdraw "${resource.title}"?`,
      description: "The resource will be removed from the marketplace.",
      confirmLabel: "Withdraw",
      onConfirm: async () => {
        const ok = await runMutation(
          () => withdrawStaffMarketplaceResource(activeType, resource.id),
          "Marketplace resource withdrawn."
        );
        if (!ok) throw new Error("Failed to withdraw resource.");
      },
    });

  const handleRestore = (resource) =>
    confirm({
      title: `Restore "${resource.title}"?`,
      description: "The resource will be published again.",
      confirmLabel: "Restore",
      onConfirm: async () => {
        const ok = await runMutation(
          () => restoreStaffMarketplaceResource(activeType, resource.id),
          "Marketplace resource restored."
        );
        if (!ok) throw new Error("Failed to restore resource.");
      },
    });

  const handleRevoke = (purchase) => {
    const reason = window.prompt(`Reason for revoking "${purchase.reference}"?`, "");
    if (reason === null) return;
    return runMutation(
      () => revokeStaffMarketplacePurchase(purchase.id, reason.trim()),
      "Purchase access revoked.",
      loadPurchases
    );
  };

  const handleRestorePurchase = (purchase) =>
    confirm({
      title: `Restore access for "${purchase.reference}"?`,
      description: "The learner will regain access to this purchase.",
      confirmLabel: "Restore access",
      onConfirm: async () => {
        const ok = await runMutation(
          () => restoreStaffMarketplacePurchase(purchase.id),
          "Purchase access restored.",
          loadPurchases
        );
        if (!ok) throw new Error("Failed to restore purchase.");
      },
    });

  const activeTypeLabel =
    MARKETPLACE_TYPES.find((type) => type.key === activeType)?.label || "Resources";

  return (
    <div>
      <StaffNavBar
        title="Marketplace"
        context="Moderate marketplace resources and manage purchase access."
      />
      <AdminPageBody>
        {message && (
          <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
            {message}
          </div>
        )}
        {staffMarketplaceMutationError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {staffMarketplaceMutationError}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <button
            className={tab === "resources" ? adminPrimaryButton : adminButton}
            onClick={() => setTab("resources")}
          >
            Resources
          </button>
          <button
            className={tab === "purchases" ? adminPrimaryButton : adminButton}
            onClick={() => setTab("purchases")}
          >
            Purchases
          </button>
        </div>

        {tab === "resources" ? (
          <>
            <div className="flex flex-wrap gap-2">
              {MARKETPLACE_TYPES.map((type) => (
                <button
                  key={type.key}
                  className={activeType === type.key ? adminPrimaryButton : adminButton}
                  onClick={() => {
                    setActiveType(type.key);
                    setPage(1);
                  }}
                >
                  {type.label}
                </button>
              ))}
            </div>

            <AdminPanel className="overflow-hidden">
              <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 p-4">
                <input
                  className={`${inputClass} max-w-xs`}
                  placeholder="Search resources"
                  value={search}
                  onChange={(event) => {
                    setPage(1);
                    setSearch(event.target.value);
                  }}
                />
                <select
                  className={`${inputClass} max-w-[180px]`}
                  value={status}
                  onChange={(event) => {
                    setPage(1);
                    setStatus(event.target.value);
                  }}
                >
                  {RESOURCE_STATUSES.map((value) => (
                    <option key={value || "all"} value={value}>
                      {value || "All statuses"}
                    </option>
                  ))}
                </select>
                <select
                  className={`${inputClass} max-w-[190px]`}
                  value={sort}
                  onChange={(event) => {
                    setPage(1);
                    setSort(event.target.value);
                  }}
                >
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="price_asc">Price (low to high)</option>
                  <option value="price_desc">Price (high to low)</option>
                </select>
                <button className={`${adminButton} ml-auto`} onClick={loadResources}>
                  Refresh
                </button>
              </div>

              {staffMarketplaceResourcesError && (
                <p className="px-4 py-3 text-sm text-red-700">
                  {staffMarketplaceResourcesError}
                </p>
              )}

              {staffMarketplaceResourcesLoading ? (
                <AdminLoadingState />
              ) : staffMarketplaceResources.length === 0 ? (
                <div className="px-4 py-12 text-center text-sm text-slate-500">
                  No {activeTypeLabel.toLowerCase()} found.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-5 py-3 font-semibold">Title</th>
                        <th className="px-5 py-3 font-semibold">Publisher</th>
                        <th className="px-5 py-3 font-semibold">Price</th>
                        <th className="px-5 py-3 font-semibold">Status</th>
                        <th className="px-5 py-3 font-semibold">Submitted</th>
                        <th className="px-5 py-3 text-center font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {staffMarketplaceResources.map((resource) => {
                        const state = String(resource.status || "").toUpperCase();
                        const canApprove = state !== "APPROVED" && state !== "WITHDRAWN";
                        const canWithdraw = state === "APPROVED" || state === "PENDING";
                        const canRestore = state === "WITHDRAWN" || state === "REJECTED";
                        return (
                          <tr key={resource.id} className="hover:bg-slate-50">
                            <td className="px-5 py-3">
                              <div className="font-medium text-slate-900">
                                {resource.title || "Untitled"}
                              </div>
                              <div className="text-xs text-slate-500">
                                {resource.slug || "—"}
                              </div>
                            </td>
                            <td className="px-5 py-3 text-slate-600">
                              {resource.publisher?.name || "—"}
                            </td>
                            <td className="px-5 py-3 text-slate-600">
                              {fmtMoney(resource.price)}
                            </td>
                            <td className="px-5 py-3">
                              <span
                                className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusClass(
                                  state
                                )}`}
                              >
                                {state || "—"}
                              </span>
                            </td>
                            <td className="px-5 py-3 text-xs text-slate-500">
                              {fmtDate(resource.submittedAt || resource.createdAt)}
                            </td>
                            <td className="px-5 py-3">
                              <div className="flex flex-wrap justify-center gap-2">
                                <button className={adminButton} onClick={() => openDetail(resource)}>
                                  Review
                                </button>
                                {canApprove && (
                                  <button
                                    className="text-xs font-semibold text-emerald-700 hover:text-emerald-900"
                                    disabled={staffMarketplaceMutationLoading}
                                    onClick={() => handleApprove(resource)}
                                  >
                                    Approve
                                  </button>
                                )}
                                {state !== "REJECTED" && (
                                  <button
                                    className="text-xs font-semibold text-amber-700 hover:text-amber-900"
                                    disabled={staffMarketplaceMutationLoading}
                                    onClick={() => handleReject(resource)}
                                  >
                                    Reject
                                  </button>
                                )}
                                {canWithdraw && (
                                  <button
                                    className="text-xs font-semibold text-slate-700 hover:text-slate-900"
                                    disabled={staffMarketplaceMutationLoading}
                                    onClick={() => handleWithdraw(resource)}
                                  >
                                    Withdraw
                                  </button>
                                )}
                                {canRestore && (
                                  <button
                                    className="text-xs font-semibold text-blue-700 hover:text-blue-900"
                                    disabled={staffMarketplaceMutationLoading}
                                    onClick={() => handleRestore(resource)}
                                  >
                                    Restore
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex items-center justify-between border-t border-slate-200 p-4 text-sm text-slate-500">
                <span>
                  Page {page} of {staffMarketplaceResourcesPages || 1} ·{" "}
                  {staffMarketplaceResourcesTotal || 0} resources
                </span>
                <div className="flex gap-2">
                  <button
                    className={adminButton}
                    disabled={page <= 1}
                    onClick={() => setPage((value) => value - 1)}
                  >
                    Previous
                  </button>
                  <button
                    className={adminButton}
                    disabled={page >= (staffMarketplaceResourcesPages || 1)}
                    onClick={() => setPage((value) => value + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            </AdminPanel>
          </>
        ) : (
          <AdminPanel className="overflow-hidden">
            <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 p-4">
              <input
                className={`${inputClass} max-w-xs`}
                placeholder="Search reference / learner"
                value={purchaseSearch}
                onChange={(event) => {
                  setPurchasePage(1);
                  setPurchaseSearch(event.target.value);
                }}
              />
              <select
                className={`${inputClass} max-w-[180px]`}
                value={purchaseStatus}
                onChange={(event) => {
                  setPurchasePage(1);
                  setPurchaseStatus(event.target.value);
                }}
              >
                {PURCHASE_STATUSES.map((value) => (
                  <option key={value || "all"} value={value}>
                    {value || "All statuses"}
                  </option>
                ))}
              </select>
              <select
                className={`${inputClass} max-w-[230px]`}
                value={purchaseType}
                onChange={(event) => {
                  setPurchasePage(1);
                  setPurchaseType(event.target.value);
                }}
              >
                {PURCHASE_TYPES.map((value) => (
                  <option key={value || "all"} value={value}>
                    {value || "All purchase types"}
                  </option>
                ))}
              </select>
              <button className={`${adminButton} ml-auto`} onClick={loadPurchases}>
                Refresh
              </button>
            </div>

            {staffMarketplacePurchasesError && (
              <p className="px-4 py-3 text-sm text-red-700">
                {staffMarketplacePurchasesError}
              </p>
            )}

            {staffMarketplacePurchasesLoading ? (
              <AdminLoadingState />
            ) : staffMarketplacePurchases.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-slate-500">
                No marketplace purchases found.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-5 py-3 font-semibold">Reference</th>
                      <th className="px-5 py-3 font-semibold">Learner</th>
                      <th className="px-5 py-3 font-semibold">Items</th>
                      <th className="px-5 py-3 font-semibold">Amount</th>
                      <th className="px-5 py-3 font-semibold">Status</th>
                      <th className="px-5 py-3 font-semibold">Email</th>
                      <th className="px-5 py-3 text-center font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {staffMarketplacePurchases.map((purchase) => {
                      const titles = (purchase.items || [])
                        .map((item) => item.title)
                        .filter(Boolean)
                        .join(", ");
                      const revoked = Boolean(purchase.revokedAt);
                      return (
                        <tr key={purchase.id} className="hover:bg-slate-50">
                          <td className="px-5 py-3 font-mono text-xs text-slate-800">
                            {purchase.reference || purchase.id}
                          </td>
                          <td className="px-5 py-3 text-slate-600">
                            <div>{purchase.learner?.name || "—"}</div>
                            <div className="text-xs text-slate-500">
                              {purchase.learner?.email || ""}
                            </div>
                          </td>
                          <td className="px-5 py-3 text-slate-600">{titles || "—"}</td>
                          <td className="px-5 py-3 text-slate-600">
                            {fmtMoney(purchase.amount)}
                          </td>
                          <td className="px-5 py-3">
                            <span
                              className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusClass(
                                purchase.status
                              )}`}
                            >
                              {purchase.status || "—"}
                            </span>
                            {revoked && (
                              <div className="mt-1 text-[11px] font-semibold text-red-600">
                                Access revoked
                              </div>
                            )}
                          </td>
                          <td className="px-5 py-3 text-xs text-slate-500">
                            <div>{purchase.emailStatus || "—"}</div>
                            {purchase.emailLastError && (
                              <div className="text-red-600">{purchase.emailLastError}</div>
                            )}
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex justify-center gap-2">
                              {revoked ? (
                                <button
                                  className={adminButton}
                                  disabled={staffMarketplaceMutationLoading}
                                  onClick={() => handleRestorePurchase(purchase)}
                                >
                                  Restore access
                                </button>
                              ) : (
                                <button
                                  className={adminDangerButton}
                                  disabled={staffMarketplaceMutationLoading}
                                  onClick={() => handleRevoke(purchase)}
                                >
                                  Revoke access
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex items-center justify-between border-t border-slate-200 p-4 text-sm text-slate-500">
              <span>
                Page {purchasePage} of {staffMarketplacePurchasesPages || 1} ·{" "}
                {staffMarketplacePurchasesTotal || 0} purchases
              </span>
              <div className="flex gap-2">
                <button
                  className={adminButton}
                  disabled={purchasePage <= 1}
                  onClick={() => setPurchasePage((value) => value - 1)}
                >
                  Previous
                </button>
                <button
                  className={adminButton}
                  disabled={purchasePage >= (staffMarketplacePurchasesPages || 1)}
                  onClick={() => setPurchasePage((value) => value + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </AdminPanel>
        )}
      </AdminPageBody>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4">
          <section className="my-auto w-full max-w-3xl rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="m-0 text-lg font-semibold text-slate-900">
                  {selected.title || "Marketplace resource"}
                </h2>
                <p className="m-0 mt-1 text-xs text-slate-500">
                  {selected.slug || selected.id} · {selected.status || "—"}
                </p>
                <p className="m-0 mt-1 text-xs text-slate-500">
                  Publisher: {selected.publisher?.name || "—"}
                  {selected.publisher?.email ? ` (${selected.publisher.email})` : ""}
                </p>
              </div>
              <button className={adminButton} onClick={() => setSelected(null)}>
                Close
              </button>
            </div>

            {staffMarketplaceResourceDetailLoading ? (
              <AdminLoadingState />
            ) : staffMarketplaceResourceDetailError ? (
              <p className="text-sm text-red-700">{staffMarketplaceResourceDetailError}</p>
            ) : (
              <form className="grid gap-3 sm:grid-cols-2" onSubmit={handleSave}>
                {[
                  { key: "title", label: "Title" },
                  { key: "coverImage", label: "Cover image URL" },
                  { key: "price", label: "Price", type: "number" },
                  { key: "fieldOfStudy", label: "Field of study" },
                  { key: "summary", label: "Summary" },
                  { key: "description", label: "Description" },
                  { key: "abstract", label: "Abstract" },
                  { key: "chapterOnePreview", label: "Chapter one preview" },
                ].map((field) => (
                  <label
                    key={field.key}
                    className="text-xs font-semibold text-slate-600"
                  >
                    {field.label}
                    <input
                      className={`${inputClass} mt-1`}
                      type={field.type || "text"}
                      value={form[field.key]}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          [field.key]: event.target.value,
                        }))
                      }
                    />
                  </label>
                ))}

                <label className="text-xs font-semibold text-slate-600">
                  Academic level
                  <select
                    className={`${inputClass} mt-1`}
                    value={form.academicLevel}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        academicLevel: event.target.value,
                      }))
                    }
                  >
                    {ACADEMIC_LEVELS.map((value) => (
                      <option key={value || "none"} value={value}>
                        {value || "Not set"}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="text-xs font-semibold text-slate-600">
                  File format
                  <select
                    className={`${inputClass} mt-1`}
                    value={form.fileFormat}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        fileFormat: event.target.value,
                      }))
                    }
                  >
                    {FILE_FORMATS.map((value) => (
                      <option key={value || "none"} value={value}>
                        {value || "Not set"}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="text-xs font-semibold text-slate-600 sm:col-span-2">
                  What is included (one item per line)
                  <textarea
                    className={`${inputClass} mt-1`}
                    rows={4}
                    value={form.whatIsIncluded}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        whatIsIncluded: event.target.value,
                      }))
                    }
                  />
                </label>

                <div className="flex flex-wrap gap-2 sm:col-span-2">
                  <button
                    className={adminPrimaryButton}
                    disabled={staffMarketplaceMutationLoading}
                  >
                    {staffMarketplaceMutationLoading ? "Saving…" : "Save changes"}
                  </button>
                  <button
                    type="button"
                    className={adminButton}
                    onClick={() => setSelected(null)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {!staffMarketplaceResourceDetailLoading && !staffMarketplaceResourceDetailError && (
              <div className="mt-5 flex flex-wrap gap-2 border-t border-slate-200 pt-4">
                <button
                  className={adminPrimaryButton}
                  disabled={staffMarketplaceMutationLoading}
                  onClick={() =>
                    runMutation(
                      () => approveStaffMarketplaceResource(activeType, selected.id),
                      "Marketplace resource approved."
                    ).then((ok) => ok && setSelected(null))
                  }
                >
                  Approve
                </button>
                <button
                  className={adminDangerButton}
                  disabled={staffMarketplaceMutationLoading}
                  onClick={handleDetailReject}
                >
                  Reject
                </button>
                <button
                  className={adminButton}
                  disabled={staffMarketplaceMutationLoading}
                  onClick={() =>
                    runMutation(
                      () => restoreStaffMarketplaceResource(activeType, selected.id),
                      "Marketplace resource restored."
                    ).then((ok) => ok && setSelected(null))
                  }
                >
                  Restore
                </button>
                <button
                  className={adminButton}
                  disabled={staffMarketplaceMutationLoading}
                  onClick={() =>
                    runMutation(
                      () => withdrawStaffMarketplaceResource(activeType, selected.id),
                      "Marketplace resource withdrawn."
                    ).then((ok) => ok && setSelected(null))
                  }
                >
                  Withdraw
                </button>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
